// chat/voiceTools.ts — THE SEAM between a live voice call and everything this app can do.
//
// runVoiceTool(name, args) has the SAME signature in every Oriora app; the call into toAction
// inside it does NOT — every app shapes toAction differently, which is exactly what broke the
// fleet chat codemod (2026-08-13). This app's shape: toAction(serverChat, customerSaid, null).
//
// Voice therefore runs the SAME path a typed message takes: name+args → ChatAction →
// executeAction. No second implementation to drift from the chat.
//
// SAFETY: the model is told to ask out loud before anything destructive, but a spoken "yes" is
// NOT a security boundary — it can mishear. Destructive verbs are re-checked HERE.
// ⚠️ `confirmed` arrives as the STRING "true", not a boolean (measured against Gemini Live,
// 2026-08-13) — a strict `=== true` check silently blocks every voice deletion.
import { toAction } from "./brain";
import { executeAction } from "./actions";

export interface VoiceToolResult {
  ok: boolean;
  message: string;
  changed?: boolean;
}

const DESTRUCTIVE = /^(delete|remove|merge|clear|reset|restore|import|wipe)|_(delete|remove|merge|restore)$/i;

const isConfirmed = (a: Record<string, unknown>): boolean =>
  a["confirmed"] === true || String(a["confirmed"] ?? "").toLowerCase() === "true" ||
  String(a["confirm"] ?? "").toLowerCase() === "yes";

/** Run ONE in-call tool. Never throws — a live call must not die because a tool failed. */
export async function runVoiceTool(
  name: string,
  args: Record<string, unknown>,
  customerSaid = "",
  _sessionId = "",
): Promise<VoiceToolResult> {
  try {
    // cloud_* verbs are enforced on the SERVER against the customer's own transcribed words.
    // Do NOT gate them on a model-written param here: that is the fictitious-consent pattern the
    // security review caught in chat-brain (2026-08-13), and it would also block a legitimate
    // spoken "yes" whenever the model simply omitted the flag.
    if (!name.startsWith("cloud_") && DESTRUCTIVE.test(name) && !isConfirmed(args)) {
      return { ok: false, message: "Ask me to confirm that out loud first." };
    }

    const r = await toAction({ action: name, params: args, reply: "" }, customerSaid, null);
    const acts = [r.action].filter((a) => a && a.kind !== "none");
    if (!acts.length) {
      const first = r.action as { message?: string } | undefined;
      return { ok: false, message: first?.message ?? "I can't do that one yet." };
    }

    let changed = false;
    let message = "";
    for (const a of acts) {
      const out = await executeAction(a);
      if (out?.message) message = out.message;
      if (out?.changed) changed = true;
    }
    return { ok: true, message: message || "Done.", changed };
  } catch (e) {
    // Report the REAL failure — the transcript must never show a success the app didn't perform.
    return { ok: false, message: e instanceof Error ? e.message : "That didn't work." };
  }
}
